import { useState, FormEvent, useEffect } from 'react';
import Modal from '../../components/Modal';
import { mapsApi, ApiError } from '../../services/api';
import { useEditor } from '../../contexts/EditorContext';
import './CreateMapModal.css';

interface CreateMapModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal for creating a new map
 * Auto-generates ID from label input
 * Folder can be set later via the map browser interface
 * @param isOpen - Controls modal visibility
 * @param onClose - Callback when modal is closed
 * @returns The rendered create map modal component
 */
const DEFAULT_SCALE = 128; // 1 meter = 128 pixels

function CreateMapModal({ isOpen, onClose }: CreateMapModalProps): JSX.Element {
  const { refreshMaps } = useEditor();
  const [label, setLabel] = useState<string>('');
  const [id, setId] = useState<string>('');
  const [idManuallyEdited, setIdManuallyEdited] = useState<boolean>(false);
  const [scaleInput, setScaleInput] = useState<string>(DEFAULT_SCALE.toString());
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Parse scale from input string
  const scale = parseFloat(scaleInput) || 0;

  /**
   * Generates a valid ID from a label string
   * Converts to lowercase, replaces spaces with hyphens, removes invalid chars
   * @param labelValue - The label to convert
   * @returns A valid map ID
   */
  const generateIdFromLabel = (labelValue: string): string => {
    return labelValue
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9_-]/g, '')
      .substring(0, 50);
  };

  /**
   * Handles label input change
   * Auto-updates ID if not manually edited
   */
  const handleLabelChange = (value: string): void => {
    setLabel(value);
    if (!idManuallyEdited) {
      setId(generateIdFromLabel(value));
    }
  };

  /**
   * Handles ID input change
   * Marks ID as manually edited
   */
  const handleIdChange = (value: string): void => {
    setIdManuallyEdited(true);
    setId(value.toLowerCase().replace(/[^a-z0-9_-]/g, ''));
  };

  /**
   * Handles form submission
   * @param event - Form submit event
   */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError('');

    if (!label.trim()) {
      setError('Label is required');
      return;
    }

    if (!id.trim()) {
      setError('ID is required');
      return;
    }

    if (id.length < 2) {
      setError('ID must be at least 2 characters');
      return;
    }

    if (scale < 0.00001 || scale > 1024) {
      setError('Scale must be between 0.00001 and 1024 pixels per meter');
      return;
    }

    setIsSubmitting(true);

    try {
      await mapsApi.create(id, label.trim(), scale);
      await refreshMaps();
      handleClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to create map');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles scale input change
   * Uses text input to allow typing decimal values freely (e.g., 0.00001)
   */
  const handleScaleChange = (value: string): void => {
    // Allow empty, numbers, and decimal notation
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setScaleInput(value);
    }
  };

  /**
   * Resets form state and closes modal
   */
  const handleClose = (): void => {
    setLabel('');
    setId('');
    setIdManuallyEdited(false);
    setScaleInput(DEFAULT_SCALE.toString());
    setError('');
    onClose();
  };

  /**
   * Reset form when modal opens
   */
  useEffect(() => {
    if (isOpen) {
      setLabel('');
      setId('');
      setIdManuallyEdited(false);
      setScaleInput(DEFAULT_SCALE.toString());
      setError('');
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Map">
      <form className="create-map-form" onSubmit={handleSubmit}>
        {error && <div className="create-map-error">{error}</div>}

        <div className="create-map-field">
          <label htmlFor="map-label">Label</label>
          <input
            id="map-label"
            type="text"
            placeholder="My Awesome Map"
            value={label}
            onChange={(e) => handleLabelChange(e.target.value)}
            disabled={isSubmitting}
            autoFocus
          />
        </div>

        <div className="create-map-field">
          <label htmlFor="map-id">ID</label>
          <input
            id="map-id"
            type="text"
            placeholder="my-awesome-map"
            value={id}
            onChange={(e) => handleIdChange(e.target.value)}
            disabled={isSubmitting}
          />
          <span className="create-map-hint">Lowercase, no spaces. Used in URLs and file system.</span>
        </div>

        <div className="create-map-field">
          <label htmlFor="map-scale">Scale (pixels per meter)</label>
          <input
            id="map-scale"
            type="text"
            inputMode="decimal"
            placeholder="128"
            value={scaleInput}
            onChange={(e) => handleScaleChange(e.target.value)}
            disabled={isSubmitting}
          />
          <span className="create-map-hint">
            {scale > 0 && scale >= 1
              ? `1m = ${scale}px. Common: 8 (large), 32 (medium), 128 (detailed)`
              : scale > 0
                ? `1px = ${(1 / scale).toLocaleString()}m. For planetary/world maps.`
                : 'Enter a scale value'
            }
          </span>
        </div>

        <button type="submit" className="create-map-submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create Map'}
        </button>
      </form>
    </Modal>
  );
}

export default CreateMapModal;
